/**
 * The score band shared by the scorecard and its detail drawer.
 *
 * It lives apart from both because both need it: the card shows the band as a tag, and the drawer repeats
 * that tag in its header. Leaving it in either one would make the other import its sibling.
 */

// Numeric score bands. The cutoffs are conservative — better to under-promise on strong than to
// let a mediocre dimension render green. See scorecards.md §8 for the source of truth.
export type ScoreBand = 'strong' | 'adequate' | 'weak' | 'critical' | 'unknown'

export const toScoreBand = (score: number): ScoreBand => {
  if (!Number.isFinite(score)) {
    return 'unknown'
  }
  if (score >= 0.8) {
    return 'strong'
  }
  if (score >= 0.6) {
    return 'adequate'
  }
  if (score >= 0.4) {
    return 'weak'
  }
  return 'critical'
}

export const BAND_LABELS: Record<ScoreBand, string> = {
  strong: 'Strong',
  adequate: 'Adequate',
  weak: 'Weak',
  critical: 'Critical',
  unknown: 'Unknown',
}

export const formatScorePercent = (score: number): string =>
  Number.isFinite(score) ? `${Math.round(score * 100)}%` : '—'

/**
 * The tag's full label: the score beside the band it falls in.
 *
 * An unscored dimension shows the band alone. Pairing a placeholder with a separator — "— · Unknown" —
 * reads as though a number were missing rather than never existing.
 */
export const scoreTagLabel = (score: number): string => {
  const band = toScoreBand(score)
  if (band === 'unknown') {
    return BAND_LABELS[band]
  }
  return `${formatScorePercent(score)} · ${BAND_LABELS[band]}`
}
